import { useEffect, useState } from "react";
import { HelpCircle, X, ExternalLink, Mail, ChevronDown, ChevronUp } from "lucide-react";
import { Link } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Help panel — slides in from the right when the "?" header button is clicked.
 * Shows:
 *  - Role-specific quick guide (bullets + jump links)
 *  - FAQs per major module
 *  - Contact escalation line
 * Content in en/hi/bn (translations flagged for review).
 */
export default function HelpPanel({ open, onClose }) {
    const { user } = useAuth();
    const [lang, setLang] = useState(user?.language || "en");
    const [guide, setGuide] = useState(null);
    const [faqs, setFaqs] = useState(null);
    const [contact, setContact] = useState(null);
    const [expanded, setExpanded] = useState({});

    useEffect(() => {
        if (!open) return;
        Promise.all([
            api.get(`/help/quick-guide?lang=${lang}`).then((r) => r.data),
            api.get(`/help/faqs?lang=${lang}`).then((r) => r.data),
            api.get(`/help/contact`).then((r) => r.data),
        ]).then(([g, f, c]) => {
            setGuide(g); setFaqs(f); setContact(c);
        }).catch(() => { /* soft-fail */ });
    }, [open, lang]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[60] bg-black/50" onClick={onClose} data-testid="help-panel-backdrop">
            <div
                className="absolute right-0 top-0 h-full w-full sm:w-[26rem] bg-white shadow-2xl flex flex-col"
                onClick={(e) => e.stopPropagation()}
                data-testid="help-panel"
            >
                <div className="h-14 flex items-center justify-between px-4 border-b border-gray-200 bg-[#1E3355] text-white">
                    <div className="flex items-center gap-2">
                        <HelpCircle className="w-5 h-5" />
                        <div className="font-semibold text-sm">Help &amp; Guide</div>
                    </div>
                    <div className="flex items-center gap-2">
                        <select
                            value={lang}
                            onChange={(e) => setLang(e.target.value)}
                            className="text-xs rounded-md bg-white/10 text-white border border-white/20 px-2 py-1 outline-none"
                            data-testid="help-lang"
                        >
                            <option value="en" className="text-gray-900">EN</option>
                            <option value="hi" className="text-gray-900">HI</option>
                            <option value="bn" className="text-gray-900">BN</option>
                        </select>
                        <button
                            onClick={onClose}
                            className="p-1.5 rounded hover:bg-white/10"
                            data-testid="help-close"
                            aria-label="Close help"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-6 text-sm">
                    {(guide?.translation_note || faqs?.translation_note) && (
                        <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800" data-testid="help-translation-note">
                            {guide?.translation_note || faqs?.translation_note}
                        </div>
                    )}

                    {guide && (
                        <section data-testid="help-quick-guide">
                            <div className="font-semibold text-[#1E3355] mb-2">{guide.title}</div>
                            <ul className="space-y-2 list-disc pl-5 text-gray-700">
                                {guide.bullets.map((b, i) => (
                                    <li key={i} data-testid={`help-bullet-${i}`}>{b}</li>
                                ))}
                            </ul>
                            {(guide.links || []).length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-2">
                                    {guide.links.map((l, i) => (
                                        <Link
                                            key={i}
                                            to={l.to}
                                            onClick={onClose}
                                            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-gray-100 text-[#1E3355] hover:bg-gray-200"
                                            data-testid={`help-link-${i}`}
                                        >
                                            {l.label}
                                            <ExternalLink className="w-3 h-3" />
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </section>
                    )}

                    {faqs && (
                        <section data-testid="help-faqs">
                            <div className="font-semibold text-[#1E3355] mb-2">FAQs</div>
                            <div className="space-y-2">
                                {faqs.modules.map((mod, mi) => (
                                    <div key={mi} className="border border-gray-200 rounded-md" data-testid={`faq-module-${mi}`}>
                                        <button
                                            onClick={() => setExpanded((s) => ({ ...s, [mi]: !s[mi] }))}
                                            className="w-full flex items-center justify-between px-3 py-2 text-left text-sm font-medium text-gray-800 hover:bg-gray-50"
                                            data-testid={`faq-module-toggle-${mi}`}
                                        >
                                            {mod.module}
                                            {expanded[mi] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                        </button>
                                        {expanded[mi] && (
                                            <div className="px-3 pb-3 space-y-3 text-xs text-gray-700">
                                                {mod.items.map((it, qi) => (
                                                    <div key={qi} data-testid={`faq-item-${mi}-${qi}`}>
                                                        <div className="font-medium text-gray-900">{it.q}</div>
                                                        <div className="mt-0.5 text-gray-600">{it.a}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {contact && (
                        <section className="border-t border-gray-200 pt-4" data-testid="help-contact">
                            <div className="font-semibold text-[#1E3355] mb-1">Still stuck?</div>
                            <p className="text-xs text-gray-700 mb-2">{contact.message_en}</p>
                            <div className="flex items-center gap-2 text-xs text-[#1E3355]">
                                <Mail className="w-4 h-4" />
                                <a href={`mailto:${contact.ho_notify_emails.join(",")}`} className="underline">
                                    {contact.ho_notify_emails.join(", ")}
                                </a>
                            </div>
                        </section>
                    )}
                </div>
            </div>
        </div>
    );
}
